import i18n from "@/i18n";
import type { ActionItemKind } from "@/api/types/ActionItemKind";
import type { ActionItemStatus } from "@/api/types/ActionItemStatus";
import type { BugDetail } from "@/api/types/BugDetail";
import type { BugPriority } from "@/api/types/BugPriority";
import type { BugStatus } from "@/api/types/BugStatus";
import type { DetectedBy } from "@/api/types/DetectedBy";
import type { EscapePoint } from "@/api/types/EscapePoint";
import type { Resolution } from "@/api/types/Resolution";
import type { RootCauseCategory } from "@/api/types/RootCauseCategory";
import type { Severity } from "@/api/types/Severity";

/** Lifecycle in order (docs/API.md, phase 2). */
export const BUG_STATUSES: BugStatus[] = [
  "reported",
  "triaged",
  "investigating",
  "mitigated",
  "fixed",
  "verified",
  "analysis",
  "closed",
];

export const SEVERITIES: Severity[] = ["s1", "s2", "s3", "s4"];
export const BUG_PRIORITIES: BugPriority[] = ["p0", "p1", "p2", "p3"];
export const RESOLUTIONS: Resolution[] = ["fixed", "duplicate", "not_a_bug", "wont_fix"];
export const DETECTED_BY: DetectedBy[] = ["customer", "support", "internal", "monitoring", "qa"];
export const ROOT_CAUSES: RootCauseCategory[] = [
  "requirements_misunderstood",
  "missing_requirement",
  "logic_error",
  "integration",
  "data",
  "configuration",
  "performance",
  "security",
  "regression",
  "infrastructure",
  "other",
];
export const ESCAPE_POINTS: EscapePoint[] = [
  "requirements",
  "design_review",
  "code_review",
  "test_cases_missing",
  "test_execution_missed",
  "environment_difference",
  "monitoring",
  "not_preventable",
];
export const ACTION_KINDS: ActionItemKind[] = [
  "regression_test",
  "code_fix",
  "monitoring",
  "process",
  "documentation",
  "training",
];
export const ACTION_STATUSES: ActionItemStatus[] = ["open", "in_progress", "done", "cancelled"];

const DEFAULT_PRIORITY: Record<Severity, BugPriority> = { s1: "p0", s2: "p1", s3: "p2", s4: "p3" };
export const defaultPriority = (s: Severity): BugPriority => DEFAULT_PRIORITY[s];

/** Statuses a bug can move forward to (closing has its own dialog). */
export function forwardStatuses(current: BugStatus): BugStatus[] {
  const i = BUG_STATUSES.indexOf(current);
  return BUG_STATUSES.slice(i + 1).filter((s) => s !== "closed");
}

/** Past `investigating` (or closed): it can be sent back to investigating. */
export function canReopen(current: BugStatus): boolean {
  return BUG_STATUSES.indexOf(current) > BUG_STATUSES.indexOf("investigating");
}

export const bugUrl = (projectKey: string, n: number) => `/p/${projectKey}/bugs/${n}`;

export type CloseCheck = "verified" | "rootCause" | "escape" | "postmortem";

/** What closing as fixed needs, done or not. Mirrors the server's `close_blockers`. */
export function closeChecklist(
  bug: Pick<BugDetail, "severity" | "verified_at" | "root_cause_category" | "escape_point" | "postmortem_status">,
): { check: CloseCheck; done: boolean }[] {
  const out: { check: CloseCheck; done: boolean }[] = [
    { check: "verified", done: !!bug.verified_at },
    { check: "rootCause", done: !!bug.root_cause_category },
    { check: "escape", done: !!bug.escape_point },
  ];
  if (bug.severity === "s1" || bug.severity === "s2") out.push({ check: "postmortem", done: bug.postmortem_status === "approved" });
  return out;
}

/** Minutes until the mitigation target (negative = overdue), or null when there's none. */
export function minutesToDue(dueAt: string | null, now = Date.now()): number | null {
  return dueAt ? Math.round((new Date(dueAt).getTime() - now) / 60_000) : null;
}

/** What the caller's effective project role allows (mirrors docs/API.md phase 2). */
export function bugPermissions(role: string) {
  return { edit: role !== "viewer", manage: role === "manager" };
}

// ───────────── Dates ─────────────

const pad = (n: number) => String(n).padStart(2, "0");

/** ISO → value for <input type="datetime-local"> (local time). */
export function toLocalInput(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** <input type="datetime-local"> value → ISO, or null when empty/invalid. */
export function fromLocalInput(v: string): string | null {
  if (!v) return null;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

/** Minutes → "45m", "2h 32m", "3d 4h". */
export function formatMinutes(min: number | null | undefined): string {
  if (min == null) return "—";
  const u = i18n.t("analytics.units", { returnObjects: true }) as { d: string; h: string; m: string };
  const m = Math.max(0, Math.round(min));
  if (m < 60) return `${m}${u.m}`;
  if (m < 24 * 60) {
    const h = Math.floor(m / 60);
    const r = m % 60;
    return r ? `${h}${u.h} ${r}${u.m}` : `${h}${u.h}`;
  }
  const d = Math.floor(m / (24 * 60));
  const h = Math.floor((m % (24 * 60)) / 60);
  return h ? `${d}${u.d} ${h}${u.h}` : `${d}${u.d}`;
}
